import "@/sentry";
import "@/styles/globals.css";

import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { AuthProvider } from "@/providers/auth-provider";
import { LanguageProvider } from "@/providers/language-provider";
import { useAuth } from "@/hooks/use-auth";
import { getQueryClient } from "@/lib/query-client";
import { NotFoundPage } from "@/components/not-found";

import { routeTree } from "@/routeTree.gen";

const queryClient = getQueryClient();

const router = createRouter({
  routeTree,
  context: {
    auth: undefined!,
    queryClient,
  },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  notFoundMode: "root",
  defaultNotFoundComponent: NotFoundPage,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function InnerApp() {
  const auth = useAuth();
  useEffect(() => {
    router.invalidate();
  }, [auth.isAuthenticated, auth.loading]);
  return <RouterProvider router={router} context={{ auth, queryClient }} />;
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root element not found");

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <AuthProvider>
          <InnerApp />
        </AuthProvider>
      </LanguageProvider>
      {import.meta.env.DEV ? (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      ) : null}
    </QueryClientProvider>
  </StrictMode>,
);
